import { useState } from 'react';
import { Box, Button, Flex } from '@chakra-ui/react';
import { Warning, X } from '@phosphor-icons/react';
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle
} from '@/components/ui/dialog';

export const ArchiveType = {
  Job: 'job',
  Client: 'client'
} as const;

export type ArchiveType = (typeof ArchiveType)[keyof typeof ArchiveType];

export default function ArchiveDialog({
  open,
  setOpen,
  archived,
  onArchiveClick,
  archiveType
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  archived: boolean;
  onArchiveClick: () => Promise<number>;
  archiveType: ArchiveType;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [archiveError, setArchiveError] = useState<boolean>(false);
  const [cannotArchive, setCannotArchive] = useState<boolean>(false);

  const handleArchive = async () => {
    setIsLoading(true);
    setCannotArchive(false);
    setArchiveError(false);

    try {
      const code = await onArchiveClick();
      if (code === 200) {
        setOpen(false);
      } else if (code === 42100 || code === 42101) {
        setCannotArchive(true);
      } else {
        setArchiveError(true);
      }
    } catch (error) {
      console.error('Error archiving item:', error);
      setArchiveError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogRoot
      role="alertdialog"
      placement="center"
      size="lg"
      open={open}
      onOpenChange={({ open }) => {
        setArchiveError(false);
        setOpen(open);
      }}
    >
      <DialogContent borderRadius="12px">
        <DialogHeader>
          <DialogTitle>
            {cannotArchive ? (
              <Flex alignItems="center" gap="8px">
                <Warning size={32} color="#D92D20" />
                <Box fontSize="24px" fontWeight="bold">
                  Cannot archive {archiveType}
                </Box>
              </Flex>
            ) : (
              <Box fontSize="24px" fontWeight="bold">
                {archived ? `Archiving ${archiveType}` : `Restoring ${archiveType}`}
              </Box>
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Box borderBottom="1px solid" borderColor="neutrals.100" mb="16px" />
          <Box fontSize="18px" py="16px" px="16px" mb="16px" color="neutrals.700">
            {archived
              ? cannotArchive
                ? archiveType === ArchiveType.Job
                  ? `You cannot archive this job because there are still active candidates associated with it. To archive this job, you must first finish all candidates under it.`
                  : `You cannot archive this client because there are still active jobs associated with it. To archive this client, you must first archive all jobs under it.`
                : `You are about to archive this ${archiveType}, meaning you will no longer be able to make changes to it. If you want to restore this item in the future, you can unarchive it.`
              : `You are about to restore this ${archiveType}, making it active again. Once restored, you will be able to edit and manage it as usual.`}
          </Box>
          <Box borderBottom="1px solid" borderColor="neutrals.100" />
          {archiveError && (
            <Box color="error.500" fontSize="16px" mt="16px">
              Failed to {archived ? 'archive' : 'restore'} {archiveType}. Please try again.
            </Box>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button
              bg="neutrals.0"
              w="120px"
              fontSize="16px"
              borderRadius="8px"
              borderColor="primary.900"
              variant="outline"
              color="primary.900"
              disabled={isLoading}
            >
              Cancel
            </Button>
          </DialogActionTrigger>
          {cannotArchive ? (
            <DialogActionTrigger asChild>
              <Button
                bg="primary.900"
                px="12px"
                fontSize="16px"
                borderRadius="8px"
                onClick={handleArchive}
              >
                View active {archiveType}s
              </Button>
            </DialogActionTrigger>
          ) : (
            <Button
              bg="primary.900"
              w="120px"
              fontSize="16px"
              borderRadius="8px"
              onClick={handleArchive}
              loading={isLoading}
            >
              {archived ? 'Archive' : 'Restore'}
            </Button>
          )}
        </DialogFooter>
        <DialogCloseTrigger m="12px" disabled={isLoading}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
